// Copyright (c) Ping. All rights reserved.
//
// Outgoing ringback tone, synthesized with WebAudio (no asset needed). Plays
// the classic North-American cadence — a 440+480 Hz tone, 2s on / 4s off —
// while a caller waits for the callee to answer. Single AudioContext, fully
// torn down on stop (no leak). Resumes the context (Safari/Chrome autoplay) —
// it's invoked right after the user clicks "call", so the gesture is fresh.

class Ringback {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private osc1: OscillatorNode | null = null;
  private osc2: OscillatorNode | null = null;
  private cadence: ReturnType<typeof setInterval> | null = null;
  private playing = false;

  start(): void {
    if (this.playing) return;
    const Ctor =
      typeof window !== 'undefined'
        ? window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        : undefined;
    if (!Ctor) return;
    this.playing = true;
    const ctx = new Ctor();
    this.ctx = ctx;
    void ctx.resume();

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    this.gain = gain;

    const mk = (freq: number) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = freq;
      o.connect(gain);
      o.start();
      return o;
    };
    this.osc1 = mk(440);
    this.osc2 = mk(480);

    const ring = () => {
      if (!this.gain || !this.ctx) return;
      const now = this.ctx.currentTime;
      this.gain.gain.cancelScheduledValues(now);
      this.gain.gain.setValueAtTime(0.0001, now);
      this.gain.gain.exponentialRampToValueAtTime(0.12, now + 0.05);
      this.gain.gain.setValueAtTime(0.12, now + 1.95);
      this.gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
    };
    ring();
    this.cadence = setInterval(ring, 6000); // 2s on + 4s off
  }

  stop(): void {
    this.playing = false;
    if (this.cadence) {
      clearInterval(this.cadence);
      this.cadence = null;
    }
    try {
      this.osc1?.stop();
      this.osc2?.stop();
    } catch {
      /* already stopped */
    }
    this.osc1 = null;
    this.osc2 = null;
    this.gain = null;
    const ctx = this.ctx;
    this.ctx = null;
    void ctx?.close().catch(() => undefined);
  }
}

export const ringback = new Ringback();
